import type { AssetsCursor } from '@aikoa/api'
import {
  addAssetPrice,
  type AssetPricePayload,
  type AssetRelationType,
  type Enums,
  type ParsedAsset,
} from '@aikoa/common'
import {
  keepPreviousData,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query'
import { orderBy } from 'lodash-es'
import { useCallback, useMemo } from 'react'
import { useUserId } from '~/components/user/SessionProvider'
import {
  getAllUserAssets,
  getUserAssetsPage,
  searchUserAssets,
  type AssetsManagementSortBy,
} from '~/data/assets/readAssets'
import { convertAssetRelation, deleteAssetPrice, updateAsset } from '~/data/assets/writeAssets'
import { supabase } from '~/lib/supabase'
import { trpc, trpcClient } from '~/lib/trpc'

const ASSETS_PAGE_SIZE = 5

export function userAssetsQueryKey(
  userId?: string,
  relationTypes?: Enums<'user_asset_relation'>[]
) {
  if (!relationTypes) {
    return [userId, 'assets', 'all']
  }

  return [userId, 'assets', relationTypes.join(',')]
}

export const ASSETS_MANAGEMENT_QUERY_KEY = (userId: string) => ({
  listingPaginated: (sortBy: AssetsManagementSortBy, sortDirection: 'asc' | 'desc') =>
    [userId, 'assets', 'management', 'paginated', sortBy, sortDirection] as const,
  listingSearch: (sortBy: AssetsManagementSortBy, sortDirection: 'asc' | 'desc', search: string) =>
    [userId, 'assets', 'management', 'search', sortBy, sortDirection, search] as const,
})

export type AssetsManagementFilterKey = 'prospect' | 'representation' | 'reference'

function applyAssetsFilter(
  assets: ParsedAsset[],
  filters: Set<AssetsManagementFilterKey>
): ParsedAsset[] {
  return assets.filter((asset) => {
    const relationType = asset.relations[0]?.relation_type

    const relationTypeFilters: AssetsManagementFilterKey[] = [
      'prospect',
      'representation',
      'reference',
    ]

    const activeRelationFilter = relationTypeFilters.find((key) => filters.has(key))

    if (activeRelationFilter && relationType !== activeRelationFilter) {
      return false
    }

    return true
  })
}

type UseAssetsPaginatedInput = {
  search?: string
  sortBy?: AssetsManagementSortBy
  sortDirection?: 'asc' | 'desc'
  filters?: Set<AssetsManagementFilterKey>
  enabled?: boolean
}

export function useAssetsPaginated({
  search = '',
  sortBy = 'created_at',
  sortDirection = 'desc',
  filters = new Set<AssetsManagementFilterKey>(),
  enabled = true,
}: UseAssetsPaginatedInput = {}) {
  const userId = useUserId()
  const trimmedSearch = search.trim()
  const hasSearch = trimmedSearch.length > 0

  const paginatedQuery = useInfiniteQuery({
    queryKey: ASSETS_MANAGEMENT_QUERY_KEY(userId ?? '').listingPaginated(sortBy, sortDirection),
    queryFn: ({ pageParam }) =>
      userId
        ? getUserAssetsPage({
            userId,
            offset: pageParam,
            sortBy,
            sortDirection,
          })
        : Promise.resolve({ assets: [], nextOffset: null }),
    getNextPageParam: (lastPage) => lastPage.nextOffset ?? undefined,
    initialPageParam: 0,
    enabled: !!userId && enabled && !hasSearch,
  })

  const searchQuery = useQuery({
    queryKey: ASSETS_MANAGEMENT_QUERY_KEY(userId ?? '').listingSearch(
      sortBy,
      sortDirection,
      trimmedSearch
    ),
    queryFn: () =>
      userId ? searchUserAssets({ userId, search: trimmedSearch }) : Promise.resolve([]),
    enabled: !!userId && enabled && hasSearch,
    placeholderData: keepPreviousData,
  })

  const assets = useMemo(() => {
    const baseAssets =
      (hasSearch ? searchQuery.data : paginatedQuery.data?.pages.flatMap((page) => page.assets)) ??
      []

    return applyAssetsFilter(baseAssets, filters)
  }, [hasSearch, searchQuery.data, paginatedQuery.data?.pages, filters])

  return {
    assets,
    isLoading: hasSearch ? searchQuery.isLoading : paginatedQuery.isLoading,
    isFetching: hasSearch ? searchQuery.isFetching : paginatedQuery.isFetching,
    isSearching: hasSearch,
    hasNextPage: hasSearch ? false : !!paginatedQuery.hasNextPage,
    isFetchingNextPage: hasSearch ? false : paginatedQuery.isFetchingNextPage,
    fetchNextPage: () => {
      if (hasSearch || !paginatedQuery.hasNextPage || paginatedQuery.isFetchingNextPage) {
        return
      }

      void paginatedQuery.fetchNextPage()
    },
  }
}

export function useConvertAssetRelation() {
  const queryClient = useQueryClient()
  const userId = useUserId()

  const convertAssetRelationMutation = useMutation({
    mutationFn: convertAssetRelation,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: userAssetsQueryKey(userId) })
      void queryClient.invalidateQueries({
        queryKey: [userId, 'assets', 'management'],
      })
    },
  })

  return convertAssetRelationMutation
}

export function useUpdateAsset() {
  const queryClient = useQueryClient()
  const userId = useUserId()

  return useMutation({
    mutationFn: updateAsset,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: userAssetsQueryKey(userId) })
      void queryClient.invalidateQueries({
        queryKey: [userId, 'assets', 'management'],
      })
    },
  })
}

type UseUserAssetsResult = {
  assets: ParsedAsset[] | undefined
  totalCount: number
  totalCounts?: Partial<Record<AssetRelationType, number>>
  isLoading: boolean
  refetch: () => void
  isRefetching: boolean
  fetchNextPage: () => void
  hasNextPage: boolean
  isFetchingNextPage: boolean
}

export function useUserAssets({
  relationTypes,
  includeOrganization = true,
  fetchAllCounts = false,
}: {
  includeOrganization?: boolean
  fetchAllCounts?: boolean
  relationTypes: Enums<'user_asset_relation'>[]
}): UseUserAssetsResult {
  const userId = useUserId()

  const relationType = relationTypes?.length === 1 ? relationTypes[0] : undefined

  const relationTypeForQuery =
    relationType === 'prospect' || relationType === 'representation' ? relationType : undefined

  const isEnabled = !!relationTypeForQuery

  const {
    data: trpcData,
    isLoading: trpcLoading,
    error: trpcError,
    refetch: trpcRefetch,
    isRefetching: trpcRefetching,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery({
    queryKey: [
      'property',
      'listUserProperties',
      includeOrganization,
      relationTypeForQuery,
    ] as const,
    queryFn: async ({ pageParam }) => {
      return trpcClient.property.listUserProperties.query({
        includeOrganization,
        relationType: relationTypeForQuery,
        limit: ASSETS_PAGE_SIZE,
        cursor: pageParam,
        fetchAllCounts,
      })
    },
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
    initialPageParam: undefined as AssetsCursor | undefined,
    enabled: !!userId && isEnabled,
  })

  if (trpcError) {
    console.error(trpcError)
  }

  const filteredAssets = useMemo(() => {
    if (!trpcData?.pages || !relationTypes) {
      return undefined
    }

    const flatAssets = trpcData.pages.flatMap((page) => page.items)

    const filtered = flatAssets.filter((asset) =>
      asset.relations.some((relation) => relationTypes.includes(relation.relation_type))
    )

    return orderBy(filtered, (asset) => asset.updated_at, ['desc'])
  }, [trpcData, relationTypes])

  const totalCount = trpcData?.pages[0]?.totalCount ?? 0
  const totalCounts = trpcData?.pages[0]?.totalCounts

  return {
    assets: filteredAssets,
    totalCount,
    totalCounts,
    isLoading: trpcLoading,
    refetch: trpcRefetch,
    isRefetching: trpcRefetching,
    fetchNextPage: () => {
      void fetchNextPage()
    },
    hasNextPage: hasNextPage ?? false,
    isFetchingNextPage: isFetchingNextPage,
  }
}

export function useUserOwnAssets({
  relationTypes,
  isEnabled = true,
}: {
  relationTypes?: Enums<'user_asset_relation'>[]
  isEnabled?: boolean
}) {
  const userId = useUserId()

  const { data, isLoading, refetch } = useQuery({
    queryKey: userAssetsQueryKey(userId, relationTypes),
    queryFn: () => (userId ? getAllUserAssets({ userId, relationTypes }) : []),
    enabled: !!userId && isEnabled,
  })

  return {
    assets: data?.filter((asset) => (asset.prices || [])?.length !== 0),
    totalCount: data?.length ?? 0,
    isLoading,
    refetch,
  }
}

export function useAsset(
  assetId: string,
  pollingOptions?: {
    pollingInterval?: number
    shouldPoll?: (data: unknown) => boolean
  }
) {
  const userId = useUserId()

  const {
    data: asset,
    isLoading,
    isRefetching,
    error,
    refetch,
    promise,
  } = useQuery(
    trpc.property.getAssetDetails.queryOptions(
      { assetId },
      {
        enabled: !!userId && !!assetId,
        refetchInterval: pollingOptions
          ? ({ state: { data } }) => {
              const asset = data?.items?.[0] ?? null

              if (!pollingOptions?.shouldPoll || pollingOptions?.shouldPoll(asset)) {
                return pollingOptions?.pollingInterval || 2000
              }

              return false
            }
          : false,
        select: (data) => data?.items?.[0] ?? null,
      }
    )
  )

  if (error) {
    console.error(error)
  }

  return { asset, isLoading, isRefetching, refetch, promise }
}

export function useAddAssetPrice(assetId: string) {
  const queryClient = useQueryClient()

  const addAssetPriceMutation = useMutation({
    mutationFn: (assetPrice: Omit<AssetPricePayload, 'assetId'>) =>
      addAssetPrice({ ...assetPrice, assetId }, { supabase }),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: trpc.property.getAssetDetails.queryKey({ assetId }),
      })
    },
  })

  return addAssetPriceMutation
}

export function useDeleteAssetPrice() {
  return useMutation({
    mutationFn: deleteAssetPrice,
  })
}

export function useInvalidateAssetQueries(assetIdTop?: string) {
  const queryClient = useQueryClient()
  const userId = useUserId()

  const invalidateAssetQueries = useCallback(
    (assetIdInner?: string) => {
      const assetId = assetIdTop ?? assetIdInner

      if (assetId) {
        void queryClient.invalidateQueries({
          queryKey: trpc.property.getAssetDetails.queryKey({ assetId }),
        })
      }

      void queryClient.invalidateQueries({ queryKey: userAssetsQueryKey(userId) })
      void queryClient.invalidateQueries({
        queryKey: [userId, 'assets', 'management'],
      })
    },
    [queryClient, assetIdTop, userId]
  )

  return invalidateAssetQueries
}

/* eslint-disable i18next/no-literal-string */

export const useDeleteAsset = () => {
  const invalidateAssetQueries = useInvalidateAssetQueries()

  return useMutation({
    mutationFn: async (assetId: string) => {
      // Delete the asset. Most relations are expected to cascade.
      const { error: assetError } = await supabase.from('assets').delete().eq('id', assetId)

      if (assetError) {
        throw new Error(`Error deleting asset: ${assetError.message}`)
      }

      const { error: traitError } = await supabase
        .from('trait_assignments')
        .delete()
        .eq('object_id', assetId)

      if (traitError) {
        throw new Error(`Error deleting trait assignments: ${traitError.message}`)
      }
    },
    onSuccess: (_, assetId) => {
      void invalidateAssetQueries(assetId)
    },
  })
}
